import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, Form, Button, Spinner, Badge, Alert, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaDownload,
  FaInfoCircle,
  FaSyncAlt,
  FaFilter,
  FaFileInvoiceDollar,
  FaFileInvoice,
  FaUsers,
  FaBook,
  FaCheckCircle,
  FaCircle,
  FaExclamationTriangle,
} from "react-icons/fa";
import HeaderFirst from "utils/HeaderFirst";
import { toastApiErrors } from "utils/toastApiError";
import { useImportOdooDataMutation, useGetOdooMigrationLogsQuery } from "actions/odooMigration/mutations";
import {
  generatePartnersTemplate,
  generateMovesTemplate,
  generateMoveLinesTemplate,
  generateAccountsTemplate,
} from "utils/excelTemplateOdoo";
import "./migrationOdoo.css";
import { useDossierRole } from "contexts/DossierRoleContext";

const GREEN = "#009688";
const GREEN_DARK = "#068f7a";

const TEMPLATE_GENERATORS = {
  move_lines_file: generateMoveLinesTemplate,
  moves_file: generateMovesTemplate,
  accounts_file: generateAccountsTemplate,
  partners_file: generatePartnersTemplate,
};

const FILE_INSTRUCTIONS = {
  move_lines_file: [
    "Colonnes : Date, Numero, Compte, Partenaire, Libelle, Debit, Credit",
    "OBLIGATOIRE : c'est le fichier qui contient toute votre comptabilite.",
    "Chaque ligne est importee et regroupee par numero de piece.",
    "Clients (411xxx), fournisseurs (401xxx), banque (521xxx) et caisse (571xxx) sont crees automatiquement.",
    "Si vous n'importez pas le fichier factures, vos factures (INV/BILL) deviennent automatiquement des Entrées/Sorties.",
  ],
  moves_file: [
    "Colonnes : Numéro, Partenaire, Journal, Date de facturation, Échéance, Montant hors taxes, Taxe, Total, Montant du",
    "Optionnel : importez vos factures pour retrouver la liste des ventes et achats avec les échéances.",
    "Les clients et fournisseurs sont crees automatiquement s'ils n'existent pas.",
    "Les factures importees ne regenerent PAS les ecritures comptables (deja importees via le fichier des ecritures).",
    "Le suivi des impayés (Montant dû) est conservé.",
  ],
  accounts_file: [
    "Colonnes : Code, Nom du compte",
    "Optionnel : importez le plan comptable complet de votre Odoo.",
    "Tous les comptes sont crees dans le plan du dossier.",
  ],
  partners_file: [
    "Colonnes : Nom d'affichage, E-mail, Telephone, Rue, Ville",
    "Optionnel : importez vos contacts Odoo en plus de la creation automatique.",
  ],
};

const FILE_TYPES = [
  {
    key: "move_lines_file",
    label: "1. Écritures comptables (OBLIGATOIRE)",
    sublabel: "Lignes d'écritures Odoo (account.move.line) - Toute votre comptabilité : historique, plan comptable, tiers",
    accept: ".xlsx,.csv",
    required: true,
    icon: FaFileInvoiceDollar,
    iconColor: "#009688",
  },
  {
    key: "moves_file",
    label: "2. Factures (optionnel)",
    sublabel: "Pièces comptables Odoo (account.move) - Liste de vos ventes et achats avec échéances et impayés",
    accept: ".xlsx,.csv",
    icon: FaFileInvoice,
    iconColor: "#0d6efd",
  },
  {
    key: "accounts_file",
    label: "3. Plan comptable (optionnel)",
    sublabel: "Comptes Odoo (account.account) - Votre plan comptable complet",
    accept: ".xlsx,.csv",
    icon: FaBook,
    iconColor: "#6f42c1",
  },
  {
    key: "partners_file",
    label: "4. Contacts (optionnel)",
    sublabel: "Contacts Odoo (res.partner) - Vos clients et fournisseurs",
    accept: ".xlsx,.csv",
    icon: FaUsers,
    iconColor: "#fd7e14",
  },
];

const STATUS_COLORS = {
  success: "success",
  skipped: "secondary",
  error: "danger",
};

const STATUS_LABELS = {
  success: "Importe",
  skipped: "Ignore",
  error: "Erreur",
};

const TYPE_LABELS = {
  partners: "Contact",
  journals: "Journal",
  moves: "Facture",
  move_lines: "Écriture",
  accounts: "Compte",
};

const cardStyle = {
  fontFamily: "Montserrat",
  borderRadius: "8px",
  border: "1px solid #e0e0e0",
};

const headerStyle = {
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "16px",
  color: "#000000",
  borderBottom: `2px solid ${GREEN}`,
  padding: "12px 16px",
};

const labelStyle = {
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "14px",
  color: "#000000",
};

const sublabelStyle = {
  fontFamily: "Montserrat",
  fontWeight: 400,
  fontSize: "12px",
  color: "#6c757d",
};

const MigrationOdooPage = ({ hideBack = false, embedded = false }) => {
  const { canEdit } = useDossierRole();
  const { clientID: dossierId } = useParams();
  const [files, setFiles] = useState({
    move_lines_file: null,
    moves_file: null,
    accounts_file: null,
    partners_file: null,
  });
  const [result, setResult] = useState(null);
  const [showInstructions, setShowInstructions] = useState({
    move_lines_file: false,
    moves_file: false,
    accounts_file: false,
    partners_file: false,
  });
  const [downloading, setDownloading] = useState(null);
  const [importOdooData, { isLoading }] = useImportOdooDataMutation();

  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(10);
  const [logsTypeFilter, setLogsTypeFilter] = useState("");

  const {
    data: logsData,
    refetch: refetchLogs,
    isFetching: logsFetching,
  } = useGetOdooMigrationLogsQuery(
    { dossier_id: dossierId },
    { skip: !dossierId, refetchOnMountOrArgChange: true }
  );

  const handleFileChange = (key, event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFiles((prev) => ({ ...prev, [key]: selectedFile }));
  };

  const handleDownloadTemplate = async (key) => {
    const generator = TEMPLATE_GENERATORS[key];
    if (!generator) return;
    setDownloading(key);
    try {
      await generator();
      toast.success("Modèle téléchargé avec succès");
    } catch (err) {
      toast.error("Erreur lors du téléchargement du modèle");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const toggleInstructions = (key) => {
    setShowInstructions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleImport = async () => {
    if (!files.move_lines_file) {
      toast.error("Le fichier des écritures comptables est obligatoire.");
      return;
    }

    const formData = new FormData();
    formData.append("dossier_id", dossierId);
    Object.entries(files).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });

    try {
      const response = await importOdooData(formData).unwrap();
      setResult(response);
      refetchLogs();
      toast.success(
        `Import termine : ${response.summary.created} cree(s), ${response.summary.skipped} ignore(s), ${response.summary.errors} erreur(s)`
      );
    } catch (err) {
      toastApiErrors(err);
    }
  };

  const handleReset = () => {
    setFiles({
      move_lines_file: null,
      moves_file: null,
      accounts_file: null,
      partners_file: null,
    });
    setResult(null);
  };

  const allLogs = logsData?.logs || [];
  const logsCount = logsData?.count ?? allLogs.length;

  // Filtrage par type cote client (le backend renvoie jusqu'a 200 logs)
  const filteredLogs = logsTypeFilter
    ? allLogs.filter((l) => l.import_type === logsTypeFilter)
    : allLogs;

  // Pagination cote client
  const logsTotalPages = Math.max(1, Math.ceil(filteredLogs.length / logsPageSize));
  const safePage = Math.min(logsPage, logsTotalPages);
  const pagedLogs = filteredLogs.slice(
    (safePage - 1) * logsPageSize,
    safePage * logsPageSize
  );

  // Stats rapides sur l'historique
  const logsStats = allLogs.reduce(
    (acc, l) => {
      acc.total += 1;
      if (l.status === "success") acc.success += 1;
      else if (l.status === "error") acc.error += 1;
      else if (l.status === "partial") acc.partial += 1;
      return acc;
    },
    { total: 0, success: 0, error: 0, partial: 0 }
  );

  // Reinitialiser la page quand le filtre change
  useEffect(() => {
    setLogsPage(1);
  }, [logsTypeFilter, logsPageSize]);

  return (
    <div>
      {!embedded && <HeaderFirst
        nom={
          <div style={{ display: "flex" }}>
            <h4 className="titre-dossier">Migration Odoo</h4>
          </div>
        }
        description={<div>Importer les donnees depuis Odoo vers Easy-Compta</div>}
        hideBack={hideBack}
      />}

      <div style={{ padding: embedded ? "4px 0" : "20px", fontFamily: "Montserrat" }}>
        {/* Section Upload */}
        <Card className="mb-4" style={cardStyle}>
          <div style={headerStyle}>Importer des fichiers Odoo</div>
          <Card.Body>
            <Alert
              style={{
                fontFamily: "Montserrat",
                fontSize: "13px",
                backgroundColor: "#e8f5f3",
                borderColor: GREEN,
                color: GREEN_DARK,
              }}
            >
              <strong>Migration Odoo vers Easy-Compta</strong>
              {" - Importez d'abord vos écritures comptables (obligatoire), puis vos factures (optionnel). Les clients, fournisseurs et comptes de paiement sont créés automatiquement."}
            </Alert>

            {/* Barre de progression des fichiers selectionnes */}
            <div className="d-flex align-items-center mb-4 p-3 rounded" style={{ backgroundColor: "#f8f9fa", fontFamily: "Montserrat" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, marginRight: "12px", whiteSpace: "nowrap" }}>
                Progression :
              </span>
              <div className="d-flex align-items-center flex-wrap gap-3">
                {FILE_TYPES.map((type, idx) => {
                  const Icon = type.icon;
                  const isSelected = !!files[type.key];
                  return (
                    <div key={type.key} className="d-flex align-items-center" style={{ fontSize: "12px" }}>
                      {idx > 0 && <span className="me-3 text-muted">→</span>}
                      <span
                        className="d-inline-flex align-items-center"
                        style={{
                          color: isSelected ? GREEN_DARK : "#adb5bd",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {isSelected ? (
                          <FaCheckCircle className="me-1" style={{ color: GREEN }} />
                        ) : type.required ? (
                          <FaExclamationTriangle className="me-1" style={{ color: "#dc3545" }} />
                        ) : (
                          <FaCircle className="me-1" style={{ fontSize: "8px" }} />
                        )}
                        <Icon className="me-1" style={{ color: isSelected ? type.iconColor : "#adb5bd" }} />
                        <span className="d-none d-md-inline">{type.label.replace(/^\d+\.\s*/, "").split(" (")[0]}</span>
                        <span className="d-md-none">{type.key.replace("_file", "")}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
              <Badge
                pill
                bg={files.move_lines_file ? "success" : "warning"}
                text={files.move_lines_file ? undefined : "dark"}
                className="ms-auto"
                style={{ fontSize: "12px", padding: "6px 12px" }}
              >
                {Object.values(files).filter(Boolean).length}/{FILE_TYPES.length} fichiers
              </Badge>
            </div>

            <div className="row">
              {FILE_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = !!files[type.key];
                return (
                  <div className="col-md-6 mb-3" key={type.key}>
                  <Card
                    className="h-100"
                    style={{
                      ...cardStyle,
                      border: isSelected
                        ? `2px solid ${type.iconColor}`
                        : type.required
                        ? "1px dashed #dc3545"
                        : cardStyle.border,
                    }}
                  >
                    <Card.Body>
                      <div className="mb-2 d-flex align-items-start justify-content-between">
                        <div className="flex-grow-1">
                          <div style={labelStyle} className="d-flex align-items-center">
                            <Icon className="me-2" style={{ color: type.iconColor }} />
                            {type.label}
                            {type.required && (
                              <Badge
                                bg="danger"
                                className="ms-2"
                                style={{ fontSize: "10px", verticalAlign: "middle" }}
                              >
                                REQUIS
                              </Badge>
                            )}
                          </div>
                          <div style={sublabelStyle}>{type.sublabel}</div>
                        </div>
                        {isSelected && (
                          <FaCheckCircle
                            style={{ color: GREEN, fontSize: "18px", flexShrink: 0 }}
                            title="Fichier sélectionné"
                          />
                        )}
                      </div>

                      {/* Boutons : Télécharger le modèle + Voir les instructions */}
                      <div className="d-flex gap-2 mb-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="success"
                          className="migration-odoo-template-btn"
                          onClick={() => handleDownloadTemplate(type.key)}
                          disabled={downloading === type.key}
                        >
                          {downloading === type.key ? (
                            <><Spinner as="span" size="sm" animation="border" className="me-1" />Téléchargement...</>
                          ) : (
                            <><FaDownload className="me-1" />Modèle</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="migration-odoo-instructions-btn"
                          onClick={() => toggleInstructions(type.key)}
                        >
                          <FaInfoCircle className="me-1" />
                          {showInstructions[type.key] ? "Masquer" : "Instructions"}
                        </Button>
                      </div>

                      {/* Panneau d'instructions repliable */}
                      {showInstructions[type.key] && (
                        <div className="migration-odoo-instructions-panel">
                          <ul className="mb-0">
                            {FILE_INSTRUCTIONS[type.key].map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <Form.Group className="mt-2">
                        <Form.Control
                          type="file"
                          accept={type.accept}
                          onChange={(e) => handleFileChange(type.key, e)}
                          style={{ fontFamily: "Montserrat", fontSize: "13px" }}
                        />
                      </Form.Group>
                      {files[type.key] && (
                        <Badge
                          bg="success"
                          className="mt-2"
                          style={{ fontFamily: "Montserrat", fontSize: "11px" }}
                        >
                          {files[type.key].name}
                        </Badge>
                      )}
                    </Card.Body>
                  </Card>
                </div>
                );
              })}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="success"
                className="migration-odoo-import-btn"
                onClick={handleImport}
                disabled={isLoading || !files.move_lines_file || !canEdit}
                title={!files.move_lines_file ? "Sélectionnez d'abord le fichier des écritures comptables" : undefined}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" size="sm" animation="border" className="me-2" />
                    Import en cours...
                  </>
                ) : (
                  "Importer"
                )}
              </Button>
              <Button
                variant="outline-secondary"
                className="migration-odoo-reset-btn"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reinitialiser
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Section Resultat */}
        {result && (
          <Card className="mb-4" style={cardStyle}>
            <div style={headerStyle}>{"Resultat de l'import"}</div>
            <Card.Body>
              <div className="row text-center mb-3">
                <div className="col-md-4">
                  <div
                    className="rounded p-3"
                    style={{ backgroundColor: "#e8f5f3", border: `1px solid ${GREEN}` }}
                  >
                    <h3 style={{ color: GREEN_DARK, marginBottom: 0, fontFamily: "Montserrat" }}>
                      {result.summary.created}
                    </h3>
                    <small style={{ color: "#6c757d", fontFamily: "Montserrat" }}>Cree(s)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div
                    className="rounded p-3"
                    style={{ backgroundColor: "#f0f0f0", border: "1px solid #d0d0d0" }}
                  >
                    <h3 style={{ color: "#6c757d", marginBottom: 0, fontFamily: "Montserrat" }}>
                      {result.summary.skipped}
                    </h3>
                    <small style={{ color: "#6c757d", fontFamily: "Montserrat" }}>Ignore(s)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div
                    className="rounded p-3"
                    style={{ backgroundColor: "#fde8e8", border: "1px solid #f5c2c2" }}
                  >
                    <h3 style={{ color: "#991B1B", marginBottom: 0, fontFamily: "Montserrat" }}>
                      {result.summary.errors}
                    </h3>
                    <small style={{ color: "#6c757d", fontFamily: "Montserrat" }}>Erreur(s)</small>
                  </div>
                </div>
              </div>

              {FILE_TYPES.map((type) => {
                const res = result[type.key.replace("_file", "")];
                if (!res) return null;
                return (
                  <div key={type.key} className="mb-2" style={{ fontFamily: "Montserrat", fontSize: "13px" }}>
                    <strong>{type.label} :</strong>{" "}
                    {res.error ? (
                      <Badge bg="danger">Erreur: {res.error}</Badge>
                    ) : (
                      <>
                        <Badge bg="success" className="me-1">{res.created} cree(s)</Badge>
                        <Badge bg="secondary" className="me-1">{res.skipped} ignore(s)</Badge>
                        {res.errors > 0 && <Badge bg="danger">{res.errors} erreur(s)</Badge>}
                        <span className="text-muted ms-2" style={{ fontSize: "12px" }}>
                          ({res.total_records} enregistrements lus)
                        </span>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Verification de la balance */}
              {result.verification && (
                <div
                  className="mt-3 p-3 rounded"
                  style={{
                    fontFamily: "Montserrat",
                    fontSize: "13px",
                    backgroundColor: result.verification.equilibre ? "#e8f5f3" : "#fff3cd",
                    border: `1px solid ${result.verification.equilibre ? GREEN : "#ffc107"}`,
                  }}
                >
                  <strong style={{ display: "block", marginBottom: "8px" }}>
                    {result.verification.equilibre
                      ? "✓ Balance équilibrée (débit = crédit)"
                      : "⚠ Balance déséquilibrée (débit ≠ crédit)"}
                  </strong>
                  <div className="row text-center">
                    <div className="col-md-4">
                      <strong>{result.verification.nb_ecritures}</strong>
                      <br />
                      <small className="text-muted">écritures comptables</small>
                    </div>
                    <div className="col-md-4">
                      <strong>{Number(result.verification.total_debit).toLocaleString("fr-FR")}</strong>
                      <br />
                      <small className="text-muted">Total débit</small>
                    </div>
                    <div className="col-md-4">
                      <strong>{Number(result.verification.total_credit).toLocaleString("fr-FR")}</strong>
                      <br />
                      <small className="text-muted">Total crédit</small>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <small className="text-muted">
                    Comparez ces montants avec la balance dans Odoo pour valider la migration.
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Section Historique */}
        <Card style={cardStyle}>
          <div
            className="d-flex justify-content-between align-items-center"
            style={headerStyle}
          >
            <span>
              Historique des imports
              {logsCount > 0 && (
                <Badge
                  pill
                  bg="light"
                  text="dark"
                  className="ms-2"
                  style={{ fontSize: "11px", fontWeight: 500 }}
                >
                  {logsCount} au total
                </Badge>
              )}
            </span>
            <Button
              variant="outline-success"
              size="sm"
              onClick={refetchLogs}
              disabled={logsFetching}
              style={{ fontFamily: "Montserrat", fontSize: "12px", borderColor: GREEN, color: GREEN }}
            >
              {logsFetching ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />
                  Actualisation...
                </>
              ) : (
                <>
                  <FaSyncAlt className="me-1" />
                  Rafraichir
                </>
              )}
            </Button>
          </div>
          <Card.Body>
            {allLogs.length === 0 ? (
              <p className="text-muted text-center py-4" style={{ fontFamily: "Montserrat" }}>
                Aucun import effectue pour le moment.
              </p>
            ) : (
              <>
                {/* Stats rapides */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Badge bg="success" pill style={{ fontSize: "12px", padding: "6px 10px" }}>
                    {logsStats.success} importe(s)
                  </Badge>
                  <Badge bg="warning" text="dark" pill style={{ fontSize: "12px", padding: "6px 10px" }}>
                    {logsStats.partial} partiel(s)
                  </Badge>
                  <Badge bg="danger" pill style={{ fontSize: "12px", padding: "6px 10px" }}>
                    {logsStats.error} erreur(s)
                  </Badge>
                  <Badge bg="secondary" pill style={{ fontSize: "12px", padding: "6px 10px" }}>
                    {logsStats.total} total
                  </Badge>
                </div>

                {/* Filtre par type */}
                <div className="d-flex align-items-center mb-3" style={{ fontSize: "12px" }}>
                  <FaFilter className="me-2" style={{ color: GREEN }} />
                  <span className="me-2" style={{ fontFamily: "Montserrat" }}>
                    Filtrer par type :
                  </span>
                  <Form.Select
                    size="sm"
                    value={logsTypeFilter}
                    onChange={(e) => setLogsTypeFilter(e.target.value)}
                    style={{ width: "auto", fontSize: "12px", fontFamily: "Montserrat" }}
                  >
                    <option value="">Tous les types</option>
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <Table striped bordered hover responsive size="sm" style={{ fontFamily: "Montserrat" }}>
                  <thead>
                    <tr>
                      <th style={{ fontSize: "12px" }}>Date</th>
                      <th style={{ fontSize: "12px" }}>Type</th>
                      <th style={{ fontSize: "12px" }}>Reference</th>
                      <th style={{ fontSize: "12px" }}>Statut</th>
                      <th style={{ fontSize: "12px" }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLogs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ fontSize: "12px" }}>
                          {new Date(log.created_at).toLocaleString("fr-FR")}
                        </td>
                        <td>
                          <Badge bg="info" style={{ fontSize: "11px" }}>
                            {TYPE_LABELS[log.import_type] || log.import_type}
                          </Badge>
                        </td>
                        <td style={{ fontSize: "12px" }}>{log.source_ref}</td>
                        <td>
                          <Badge bg={STATUS_COLORS[log.status] || "secondary"} style={{ fontSize: "11px" }}>
                            {STATUS_LABELS[log.status] || log.status}
                          </Badge>
                        </td>
                        <td style={{ fontSize: "12px", color: "#6c757d" }}>{log.message || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                  <div className="d-flex align-items-center" style={{ fontSize: "12px", fontFamily: "Montserrat" }}>
                    <span className="me-2">Lignes par page :</span>
                    <Form.Select
                      size="sm"
                      value={logsPageSize}
                      onChange={(e) => setLogsPageSize(Number(e.target.value))}
                      style={{ width: "auto", fontSize: "12px" }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Select>
                    <span className="ms-3 text-muted">
                      {filteredLogs.length === 0
                        ? "0"
                        : `${(safePage - 1) * logsPageSize + 1}-${Math.min(
                            safePage * logsPageSize,
                            filteredLogs.length
                          )}`}{" "}
                      sur {filteredLogs.length}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setLogsPage(1)}
                      style={{ fontSize: "12px" }}
                    >
                      «
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                      style={{ fontSize: "12px" }}
                    >
                      ‹
                    </Button>
                    <span style={{ fontSize: "12px", fontFamily: "Montserrat", padding: "0 8px" }}>
                      Page {safePage} / {logsTotalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={safePage >= logsTotalPages}
                      onClick={() => setLogsPage((p) => Math.min(logsTotalPages, p + 1))}
                      style={{ fontSize: "12px" }}
                    >
                      ›
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={safePage >= logsTotalPages}
                      onClick={() => setLogsPage(logsTotalPages)}
                      style={{ fontSize: "12px" }}
                    >
                      »
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default MigrationOdooPage;
